import { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import Trash from "src/components/ui-icons/buttons/Trash";
import { useAppSelector } from "src/hooks/redux";
import { deleteItemRequest, getItemsRequest, updateItemRequest } from "src/modules/spItems/api";
import CustomRadio from "src/pages/DashboardPage/acadia/components/Tests/components/Ui/CustomRadio";
import { SelectedCurrentUser } from "src/modules/users/types";
import { selectCurrentUser } from "src/modules/users/selector";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import Loading from "src/components/Loading";
import { MTFavoriteFiltersListName } from "src/api/config/myTasksLists.config";
import { myTasksIncidentsQuestionsListName } from "src/api/config/myTasksInc.config";
import { myTasks5WayWithRelationsListName } from "src/api/config/myTasks5Way.config";
import { myTasksAuditQuestionsListName, myTasksAuditWithRelationsListName } from "src/api/config/myTasksAudit.config";

export const FavoriteFiltersList = ({
  reRenderTableFlag,
  reRenderFilterSettings,
  setReRenderFilterSettings,
  setFiltersParams,
  type,
  setCurrentTypeInc,
  filtersParams,
  listQuestions,
  setDefaultUserFilter,
  setUserFilterCheck,
  userFilterCheck,
  setReRenderUserFilter,
  favoriteFilters,
  setFavoriteFilters,
  extraCompareData,
}: any) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const currentUser: SelectedCurrentUser = useAppSelector(selectCurrentUser);
  const [fieldsFilter, setFieldsFilter] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const pendingApplyIdRef = useRef<number | null>(null);
  const prevDefaultIdRef = useRef<number | null>(null);

  const parseJson = (value: any) => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const dateFieldsByType: Record<string, string[]> = {
    fiveWhy: ["Date5Way"],
    deviation: ["Date5Way"],
    audit: ["DateOb"],
    inc: ["DateIncidents", "DateIncidents2", "TimeIncident"],
    triggers: [],
    security: [],
  };

  const compactParams = (value: any): any => {
    if (value == null) return undefined;
    if (Array.isArray(value)) {
      const next = value
        .map((v) => compactParams(v))
        .filter((v) => v !== undefined);
      return next.length > 0 ? next : undefined;
    }
    if (typeof value === "object") {
      const next: any = {};
      Object.keys(value).forEach((k) => {
        const v = (value as any)[k];
        if (v === null || v === undefined || v === "") return;
        const compacted = compactParams(v);
        if (compacted === undefined) return;
        if (typeof compacted === "object" && !Array.isArray(compacted) && Object.keys(compacted).length === 0) return;
        next[k] = compacted;
      });
      if (
        ("startDate" in next || "endDate" in next) &&
        (next.startDate == null || next.startDate === "") &&
        (next.endDate == null || next.endDate === "")
      ) {
        return undefined;
      }
      return Object.keys(next).length > 0 ? next : undefined;
    }
    return value;
  };

  const activeFiltersParamsObj = useMemo(() => {
    const raw = parseJson(filtersParams) ?? {};
    const merged: any = { ...raw };
    const dateFields = dateFieldsByType[type] || [];
    dateFields.forEach((key) => {
      delete merged[key];
    });
    if (extraCompareData && typeof extraCompareData === "object") {
      Object.keys(extraCompareData).forEach((k) => {
        if (extraCompareData[k] !== undefined && extraCompareData[k] !== null) {
          merged[k] = extraCompareData[k];
        }
      });
    }
    return compactParams(merged) ?? {};
  }, [filtersParams, extraCompareData, type]);

  const matchedId = useMemo(() => {
    if (!favoriteFilters || favoriteFilters.length === 0) return null;
    const dateFields = dateFieldsByType[type] || [];
    for (const f of favoriteFilters) {
      const parsed = parseJson(f.MTFullFilters) ?? {};
      dateFields.forEach((key) => {
        delete parsed[key];
      });
      const filterParamsObj = compactParams(parsed) ?? {};
      if (_.isEqual(activeFiltersParamsObj, filterParamsObj)) {
        return f.ID as number;
      }
    }
    return null;
  }, [favoriteFilters, activeFiltersParamsObj, type]);

  const selectedFilterParamsObj = useMemo(() => {
    if (selectedId == null) return null;
    const selectedFilter = favoriteFilters?.find((f: any) => f.ID === selectedId);
    if (!selectedFilter) return null;
    const parsed = parseJson(selectedFilter.MTFullFilters) ?? {};
    const dateFields = dateFieldsByType[type] || [];
    dateFields.forEach((key) => {
      delete parsed[key];
    });
    return compactParams(parsed) ?? {};
  }, [favoriteFilters, selectedId, type]);

  useEffect(() => {
    if (selectedId == null && matchedId != null) {
      setSelectedId(matchedId);
    }
  }, [matchedId, selectedId]);

  useEffect(() => {
    if (selectedId == null) return;
    if (!favoriteFilters || favoriteFilters.length === 0) return;
    if (!favoriteFilters.some((f: any) => f.ID === selectedId)) {
      setSelectedId(null);
    }
  }, [favoriteFilters, selectedId]);

  const applyFavoriteFilter = (filter: any) => {
    setSelectedId(filter.ID);
    pendingApplyIdRef.current = filter.ID;

    const displayObj = parseJson(filter.DisplayFilters) ?? {};
    if (setCurrentTypeInc && displayObj.TypeIncident) {
      setCurrentTypeInc(`${displayObj.TypeIncident}_${Math.random()}`);
    }

    const nextParams = parseJson(filter.MTFullFilters) ?? {};
    typeof setFiltersParams === "function" && setFiltersParams(nextParams);

    if (typeof setDefaultUserFilter === "function") setDefaultUserFilter(filter);
    if (typeof setUserFilterCheck === "function") setUserFilterCheck(true);
    if (typeof setReRenderUserFilter === "function") setReRenderUserFilter(Math.random());
    typeof reRenderTableFlag === "function" && reRenderTableFlag(false);

    setOpen(false);
  };

  useEffect(() => {
    if (selectedId == null) return;
    if (!selectedFilterParamsObj) return;

    if (pendingApplyIdRef.current === selectedId) {
      if (_.isEqual(activeFiltersParamsObj, selectedFilterParamsObj)) {
        pendingApplyIdRef.current = null;
      }
      return;
    }

    if (!_.isEqual(activeFiltersParamsObj, selectedFilterParamsObj)) {
      setSelectedId(null);
    }
  }, [activeFiltersParamsObj, selectedFilterParamsObj, selectedId]);

  useEffect(() => {
    if (!favoriteFilters || favoriteFilters.length === 0) return;

    const defaultFilter = favoriteFilters.find((f: any) => f.DefaultFilter);
    if (!defaultFilter) return;

    const activeHasParams = Object.keys(activeFiltersParamsObj).length > 0;

    if (prevDefaultIdRef.current === null) {
      prevDefaultIdRef.current = defaultFilter.ID;
      if (!activeHasParams && selectedId == null) {
        applyFavoriteFilter(defaultFilter);
      }
      return;
    }

    if (prevDefaultIdRef.current !== defaultFilter.ID) {
      prevDefaultIdRef.current = defaultFilter.ID;
      applyFavoriteFilter(defaultFilter);
    }
  }, [favoriteFilters]);

  const deleteFilter = (id: number) => {
    deleteItemRequest({
      listName: MTFavoriteFiltersListName,
      ID: id,
    }).then(() => {
      setReRenderFilterSettings(!reRenderFilterSettings);
    });
  };

  useEffect(() => {
    setLoading(true);
    if (Object.keys(currentUser).length > 0 && currentUser.id > 0) {
      getItemsRequest({
        listName: MTFavoriteFiltersListName,
        filters: { Author: currentUser.id, Type: type },
      }).then((results) => {
        setFavoriteFilters([...results.items]);
        setLoading(false);
      });
    }
  }, [reRenderFilterSettings, currentUser, type]);

  const updateDefaultFilterHandle = (props: any) => {
    setLoading(true);
    const { id, defaultValue, filter } = props;
    let updateFilters: any = [];
    favoriteFilters.forEach((filter: any) => {
      updateFilters = [
        ...updateFilters,
        {
          ID: filter.ID,
          DefaultFilter: false,
        },
      ];
    });
    updateItemRequest({
      listName: MTFavoriteFiltersListName,
      body: updateFilters[0],
      bodyArr: updateFilters,
    }).then(() => {
      updateItemRequest({
        listName: MTFavoriteFiltersListName,
        body: { ID: id, DefaultFilter: defaultValue },
      }).then(() => {
        let tempFilters = [...favoriteFilters];
        let updated = tempFilters.map((filter) => {
          return { ...filter, DefaultFilter: (filter.ID === id && defaultValue) || false };
        });
        setFavoriteFilters([...updated]);
        if (defaultValue) {
          const nextFilter = { ...filter, DefaultFilter: true };
          applyFavoriteFilter(nextFilter);
        }
        setLoading(false);
      });
    });
  };

  useEffect(() => {
    if (listQuestions) {
      if (listQuestions === myTasksIncidentsQuestionsListName) {
        getItemsRequest({
          listName: myTasksIncidentsQuestionsListName,
          sortOrder: { name: "Order0", asc: true },
        }).then((results) => {
          const tempIncQuestions = [...results.items];
          let incFieldsForm: any = [];
          incFieldsForm = [
            ...incFieldsForm,
            ...[
              { key: "ResponsibleEmail", title: "ФИО инициатора" },
              { key: "Location", title: "Локация, где произошло несоответствие/инцидент" },
              { key: "Department", title: "Отдел, где произошло несоответствие/инцидент" },
              { key: "Region", title: "Участок, где произошло несоответствие/инцидент" },
              { key: "Status", title: "Статус" },
              { key: "StatusActions", title: "Статус действий" },
              { key: "ClosedDate", title: "Срок на закрытие инцидента/несоответствия" },
              { key: "DateIncidents", title: "Дата регистрации несоответствия/инцидента" },
              { key: "DateIncidents2", title: "Дата несоответствия/инцидента" },
              { key: "TimeIncident", title: "Время несоответствия/инцидента" },
              { title: "Тип несоответствия/инцидента", key: "TypeIncident" },
            ],
          ];
          tempIncQuestions.forEach((item) => {
            if (item.TypeQuestion === "repeater") {
              incFieldsForm = [
                ...incFieldsForm,
                ...[
                  { title: "SAP № технического места", key: "Equipments_SAP" },
                  { title: "Название оборудования", key: "Equipments_Title" },
                  { title: "Производитель оборудования", key: "Equipments_Manufacturer" },
                  { title: "Модель оборудования", key: "Equipments_Model" },
                  { title: "Тип оборудования", key: "Equipments_TypeEquipment" },
                ],
              ];
            } else {
              incFieldsForm = [
                ...incFieldsForm,
                {
                  key: item.Key,
                  title: item.Title,
                },
              ];
            }
          });
          setFieldsFilter([...incFieldsForm]);
        });
      }
      if (listQuestions === myTasks5WayWithRelationsListName) {
        const fieldsForm = [
          { key: "Date5Way", title: "Дата" },
          { key: "Status", title: "Статус" },
          { key: "Location", title: "Локация" },
          { key: "Department", title: "Отдел" },
          { key: "Region", title: "Участок" },
          { key: "Trigger", title: (type === "fiveWhy" && "Триггер 5Почему") || "Триггер" },
          { key: "KPI", title: "KPI" },
          { key: "AuthorEmail", title: "Автор" },
          { key: "Editors", title: (type === "fiveWhy" && "Ответственный") || "Участник" },
          { key: "ProblemRepeat", title: "Повторяющаяся проблема?" },
          { key: "Plan", title: "План реагирования выполнен?" },
          { key: "Tree", title: "Корневая причина выявлена" },
          { key: "ActionsPlan", title: "Действия запланированы" },
        ];
        setFieldsFilter([...fieldsForm]);
      }
      if (listQuestions === myTasksAuditWithRelationsListName) {
        getItemsRequest({ listName: myTasksAuditQuestionsListName }).then((results) => {
          const tempIncQuestions = [...results.items];
          let incFieldsForm: any = [];
          incFieldsForm = [
            ...incFieldsForm,
            ...[
              { key: "DateOb", title: "Дата наблюдения" },
              { key: "Observer", title: "ФИО наблюдателя" },
              { key: "ObservedUser", title: "ФИО, за кем проводили наблюдение" },
              { key: "Location", title: "Локация, где проводилось наблюдение" },
              { key: "Department", title: "Отдел, где проводилось наблюдение" },
              { key: "Region", title: "Участок, где проводилось наблюдение" },
            ],
          ];
          tempIncQuestions.forEach((item) => {
            incFieldsForm = [
              ...incFieldsForm,
              {
                key: item.dc0t,
                title: item.Title,
              },
            ];
          });
          setFieldsFilter([...incFieldsForm]);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (favoriteFilters.length > 0) {
      if (!userFilterCheck && typeof setUserFilterCheck === "function") {
        setUserFilterCheck(true);
      }
    } else if (favoriteFilters.length === 0 && !loading) {
      if (!userFilterCheck && typeof setUserFilterCheck === "function") {
        setUserFilterCheck(true);
      }
    }
  }, [favoriteFilters, loading, userFilterCheck, setUserFilterCheck]);

  return (
    <>
      {(loading && (
        <FlexCol className="flex w-6 h-6">
          <Loading />
        </FlexCol>
      )) || (
        <>
          {favoriteFilters.length > 0 && (
            <div>
              {!open ? (
                <svg
                  className="cursor-pointer hover:text-darkblue"
                  width="20"
                  height="12"
                  viewBox="0 0 20 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={() => setOpen(!open)}
                >
                  <path
                    d="M1.62106 1.55277L1.62103 1.5528L1.62526 1.55693L9.65026 9.39443L10.0013 9.73724L10.3507 9.39276L18.3757 1.48027L18.3782 1.47777C18.5204 1.33553 18.7288 1.33553 18.8711 1.47777C19.0129 1.61959 19.0133 1.8272 18.8723 1.9694C18.8719 1.96982 18.8715 1.97025 18.8711 1.97067L10.2492 10.4426L10.2492 10.4426L10.2461 10.4457C10.1016 10.5901 10.0343 10.5992 9.99961 10.5992C9.91122 10.5992 9.83465 10.5776 9.72998 10.4979L1.12692 2.04442C0.985926 1.90221 0.986341 1.69459 1.12816 1.55277C1.2704 1.41053 1.47882 1.41053 1.62106 1.55277Z"
                    fill="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              ) : (
                <div className="relative">
                  <svg
                    className="cursor-pointer"
                    width="20"
                    height="12"
                    viewBox="0 0 20 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={() => setOpen(!open)}
                  >
                    <path
                      d="M10.3507 2.60724L10.0004 2.26193L9.64941 2.60641L1.62441 10.4814L1.62439 10.4814L1.62106 10.4847C1.47882 10.627 1.2704 10.627 1.12816 10.4847C0.986342 10.3429 0.985926 10.1353 1.12692 9.99309L9.75005 1.51992L9.75006 1.51994L9.75316 1.51683C9.8954 1.3746 10.1038 1.3746 10.2461 1.51683L10.246 1.51685L10.2492 1.51992L18.8711 9.99183C18.8715 9.99226 18.8719 9.99268 18.8723 9.9931C19.0133 10.1353 19.0129 10.3429 18.8711 10.4847C18.7968 10.559 18.6935 10.6008 18.6246 10.6008C18.5363 10.6008 18.4598 10.5792 18.3553 10.4997L10.3507 2.60724Z"
                      fill="#9CA3AF"
                      stroke="#012169"
                    />
                  </svg>
                  <div className="absolute top-5 right-0 z-[50] w-[245px] tablet:w-[600px]">
                    {favoriteFilters.map((filter: any) => (
                      <div
                        className="flex flex-col gap-5 bg-[#ffffff] p-5 rounded-lg shadow-md mb-[10px] hover:bg-[#F3F4F6]"
                        key={filter.ID}
                      >
                        {(() => {
                          const isActive = selectedId === filter.ID;
                          return (
                        <div className="flex justify-between">
                          <div className="flex gap-[8px]">
                            <button
                              onClick={() => {
                                applyFavoriteFilter(filter);
                              }}
                              className="hover:cursor-pointer"
                            >
                              <CustomRadio checked={isActive} />
                            </button>
                            <span className="text-[#111928]">{filter.Title}</span>
                          </div>
                          <button className="hover:cursor-pointer" onClick={() => deleteFilter(filter.ID)}>
                            <Trash />
                          </button>
                        </div>
                          );
                        })()}
                        <ul className="flex flex-col w-full gap-2">
                          {Object.keys(JSON.parse(filter.DisplayFilters)).map((key: string) => (
                            <li className="w-full">
                              <span className="text-[#637381]">
                                {(fieldsFilter.filter((field: any) => field.key === key).length > 0 &&
                                  fieldsFilter.filter((field: any) => field.key === key)[0]["title"]) ||
                                  key}
                                :{" "}
                              </span>
                              <span className="text-[#111928]">
                                {(Array.isArray(JSON.parse(filter.DisplayFilters)[key]) &&
                                  JSON.parse(filter.DisplayFilters)[key].some((item: any) => item.name) &&
                                  JSON.parse(filter.DisplayFilters)[key].map((item: any) => item.name).join(", ")) ||
                                  (Array.isArray(JSON.parse(filter.DisplayFilters)[key]) &&
                                    JSON.parse(filter.DisplayFilters)[key].join(", ")) ||
                                  JSON.parse(filter.DisplayFilters)[key]}
                              </span>
                            </li>
                          ))}
                          <li className="w-full">
                            <div className="flex gap-[8px]">
                              <button
                                onClick={() =>
                                  updateDefaultFilterHandle({
                                    id: filter.ID,
                                    defaultValue: !filter.DefaultFilter,
                                    filter,
                                  })
                                }
                                className="hover:cursor-pointer"
                              >
                                <CustomRadio checked={filter.DefaultFilter} />
                              </button>
                              <span className="text-[#111928]">Установить по умолчанию</span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};
